'use client';

import type { Card } from '@/modules/FinancialPlanning/ui/Widgets/Home';
import { usePathname } from 'next/navigation';
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { Box, Flex, type FlexProps, Text } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { PATHS } from '@/config/paths';
import {
  floatingInvestScreensRoutes,
  routesSHowDisclaimerEqiResearchHubs,
} from '@/config/routesConfig';
import type { HeaderProps } from '@/components/modules/Header';
import { Header } from '@/components/modules/Header';
import { Menu } from '@/components/modules/Menu';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { ProfileDrawer } from '@/modules/Customer/ui/ProfileDrawer';
import { DisclaimerResearchContent } from '@/modules/Research/ui/DisclaimerResearchContent/DisclaimerResearchContent';
import { FloatingButton } from '@/components/elements/FloatingButton';
import { InvestFloatingButton } from '@/components/elements/InvestFloatingButton';
import { IfRender } from '@/utils/jsx';

export const AuthTemplate: FC<AuthTemplateProps> = ({
  children,
  header,
  showDisclaimerContentResearch = false,
  containerProps,
  showFPFloatingButton = false,
}) => {
  const pathname = usePathname();
  const t = useT();

  const cards = t.raw('screens.financialPlanning.home.cards') as Card[];

  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: customer } = useGetCustomerStatus();

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let lastScrollTop = scrollContainer.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      setIsMenuVisible(currentScrollTop <= lastScrollTop);
      lastScrollTop = currentScrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box h="100vh" w="100vw" bgColor="background.primary" overflow="hidden">
      <IfRender condition={showFPFloatingButton}>
        <FloatingButton options={cards} />
      </IfRender>
      <IfRender condition={checkCanShowInvestFloatingButton(pathname)}>
        <InvestFloatingButton />
      </IfRender>
      <Box position="relative" zIndex="banner" bgColor="background.brandLight">
        <Header {...header} customer={customer} />
      </Box>
      <Flex
        ref={scrollContainerRef}
        flexDir="column"
        w="full"
        h="calc(100vh - 6rem)"
        pb="2xl"
        overflowY="scroll"
        overflowX="hidden"
      >
        <Flex
          position="fixed"
          zIndex={1100}
          direction="column"
          top="6rem"
          insetInline="0"
          w="full"
          h="9.75rem"
          bg="background.primary"
          transition="transform 0.25s ease"
          transform={
            isMenuVisible ? 'translateY(0)' : 'translateY(calc(-100% - 6rem))'
          }
        >
          <Flex
            direction="column"
            w="full"
            h="full"
            maxW={{ base: 'full', '2xl': '86rem' }}
            mx="auto"
            px="3xl"
          >
            <Menu isVisible={isMenuVisible} />
          </Flex>
        </Flex>
        <Flex
          flexDir="column"
          w="full"
          minH="full"
          maxW={{ base: 'full', '2xl': '86rem' }}
          mx="auto"
          px="3xl"
          pt="10rem"
        >
          <Flex
            direction="column"
            gap="2xl"
            w="full"
            {...containerProps}
            px="0"
            py="2xl"
          >
            {children}
          </Flex>
          <IfRender
            condition={routesSHowDisclaimerEqiResearchHubs.includes(pathname)}
          >
            <Text
              fontSize="m"
              color="content.secondary"
              p="2xl"
              pt="0"
              sx={{
                '& a': {
                  color: 'content.secondary',
                  fontWeight: 'semibold',
                  textDecoration: 'none',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: t('app.disclaimerEqiResearchHub'),
              }}
            />
          </IfRender>
          <IfRender condition={showDisclaimerContentResearch}>
            <DisclaimerResearchContent />
          </IfRender>
        </Flex>
      </Flex>
      <ProfileDrawer />
    </Box>
  );
};

const checkCanShowInvestFloatingButton = (pathname: string) => {
  if (pathname === PATHS.home) return false;

  const pathnameMatched = floatingInvestScreensRoutes.includes(pathname);
  if (pathnameMatched) return true;

  const [, basePath] = pathname.split('/');

  if (!basePath) return false;
  return floatingInvestScreensRoutes.some((path) => path.includes(basePath));
};

export type AuthTemplateProps = PropsWithChildren<{
  header?: HeaderProps;
  showDisclaimerContentResearch?: boolean;
  containerProps?: FlexProps;
  showFPFloatingButton?: boolean;
  hasNoPrevRoute?: boolean;
}>;
